import { AnalyticsFilter, AnalyticsEngine } from '@sprinklr/stories/analytics/AnalyticsRequest';
import { Engine } from 'models/Engine/types';
import { BulkLookupValue } from 'services/SprinklrAPIService/SprinklrAPIService';
import { WidgetFilter } from 'utils/Widget/Filter/WidgetFilter';
import { WidgetFilterAnalytics } from 'utils/Widget/Filter/WidgetFilterAnalytics';
import { WidgetWrapper } from 'utils/Widget/WidgetWrapper';

export enum DataSourceName {
    PAID = 'paid',
    BENCHMARKING = 'benchmarking',
    INBOUND_ANALYTICS = 'inboundAnalytics',
    SOCIAL_ANALYTICS = 'socialAnalytics',
    LISTENING_TAGS = 'listeningTags',
    LISTENING_TOPICS = 'listeningTopics',
    RDB_FIREHOSE = 'rdbFirehose',
    STORY_QUERY = 'storyQuery',
    STORY_TAGS = 'storyTags',
    STORY_QUERY_TAGS = 'storyQueryTags',
    LISTENING_EXPLORER = 'listeningExplorer',
    UNIVERSAL_PROFILE = 'universalProfile',
    TASK = 'task',
    CUSTOM_ENTITY = 'customEntity',
    VOICE = 'voice',
    COMMUNITY = 'community',
    DAM = 'DAM',
    AUDIENCE_ACTIVITY = 'audienceActivity',
    OUTBOUND_MESSAGE = 'outboundMessage',
    CONSUMPTION = 'consumption',
}

export enum DataSourceDefaultTimePeriod {
    RDB_FIREHOSE = 'last_24_hours',
}

export interface DataSources {
    benchmarking?: DataSource;
    inboundAnalytics?: DataSource;
    listeningTags?: DataSource;
    listeningTopics?: DataSource;
    paid?: DataSource;
    socialAnalytics?: DataSource;
    storyQuery?: DataSource;
    listeningExplorer?: DataSource;
    universalProfile?: DataSource;
    task?: DataSource;
    customEntity?: DataSource;
    voice?: DataSource;
    community?: DataSource;
    DAM?: DataSource;
    audienceActivity?: DataSource;
    outboundMessage?: DataSource;
    consumption?: DataSource;
}

export interface DataSourceNames {
    benchmarking?: string;
    inboundAnalytics?: string;
    listeningTags?: string;
    listeningTopics?: string;
    paid?: string;
    socialAnalytics?: string;
    storyQuery?: string;
    listeningExplorer?: string;
    universalProfile?: string;
    task?: string;
    customEntity?: string;
    voice?: string;
    community?: string;
    DAM?: string;
    audienceActivity?: string;
    outboundMessage?: string;
    consumption?: string;
}

export interface DataSource {
    report?: string;
    reportingEngine?: Engine;
    sourceGroup?: string;
    filters?: DataSourceFilter[];
    ancillaryFilters?: DataSourceFilter[];
    engine?: Engine;
}

export interface DataSourceFilter {
    dimensionLabel: string;
    filter: SimpleFilter;
    filterValueLabels?: FilterValueLabel[];
}

/**
 * We've got data we're getting back from metaInfoService that's typed as BulkLookupValue but at
 * runtime often has extra fields. Added ones we care about are added to the type here:
 */
export type FilterValueLabel = {
    displayName?: string;
} & BulkLookupValue;

export type SimpleFilter = Partial<AnalyticsFilter> &
    Pick<AnalyticsFilter, 'dimensionName' | 'filterType' | 'values' | 'allValues'>;

export interface DataSourceConfig {
    name: DataSourceName;
    label: string;
    subType?: string;
    engine: AnalyticsEngine;
    sourceGroup?: string;
    report: string;
    widgetWrapper?: WidgetWrapper;
    widgetFilters?: WidgetFilterAnalytics[];
    ancillaryWidgetFilters?: WidgetFilterAnalytics[];
    icon: string;
    color?: string;
    toolTip?: string;
    save?: () => void;
    revert?: () => void;
    onChange?: (
        isComplete: boolean,
        filter: WidgetFilter,
        labels: FilterValueLabel[],
        offset: number
    ) => void;
    onChangeAncillary?: (
        isComplete: boolean,
        filter: WidgetFilter,
        labels: FilterValueLabel[],
        dimLabel: string,
        offset: number
    ) => void;
    onFilterAdd?: () => void;
    onFilterDelete?: (filter: WidgetFilter, offset: number) => void;
    onFilterClear?: () => void;
    original?: DataSource;
    filterValueLabels?: FilterValueLabel[][];
    ancillaryFilterValueLabels?: FilterValueLabel[][];
    onClick?: () => void;
}

export type DataSourceCategory =
    | 'reporting'
    | 'sprinksights'
    | 'paid'
    | 'content-marketing'
    | 'twitter'
    | 'rdb-firehose';
